import { useCallback, useEffect, useRef } from "react";

// Keeps the map on the view chosen by `initialView` (lib/map.js) until the user takes over.
//
// The view can change while the map is open: the position of the user and the locations arrive
// after the map is drawn. As long as the user has not dragged or zoomed the map, it follows
// those changes; after that it is never moved on its own again.
//
// `view` is { center, zoom, source }. Returns the handlers for <GoogleMap> and `showAt`, which
// moves the map because the user asked for it (a button).
export const useMapView = (map, view) => {
  const key = [view.source, view.center.lat, view.center.lng, view.zoom].join("|");

  const touched = useRef(false);
  // The view put on the map by this hook: its key, and the zoom that was set. A zoom event is
  // the user's own only when the zoom is not the one set here.
  const applied = useRef(null);
  const latest = useRef(view);
  latest.current = view;

  // A different map instance starts from scratch
  useEffect(() => {
    touched.current = false;
    applied.current = null;
  }, [map]);

  useEffect(() => {
    if (!map || touched.current || (applied.current && applied.current.key === key)) return;
    const { center, zoom } = latest.current;
    map.setZoom(zoom);
    map.setCenter(center);
    // Google Maps may limit the zoom (e.g. a very small window): remember what it really is
    applied.current = { key, zoom: typeof map.getZoom === "function" ? map.getZoom() : zoom };
  }, [map, key]);

  const onDragStart = useCallback(() => {
    touched.current = true;
  }, []);

  const onZoomChanged = useCallback(() => {
    if (!map || !applied.current) return;
    if (map.getZoom() !== applied.current.zoom) touched.current = true;
  }, [map]);

  const showAt = useCallback((center, zoom) => {
    if (!map || !center) return;
    map.setZoom(zoom);
    map.panTo(center);
    // The user asked for this view: from now on the map is never moved on its own
    touched.current = true;
  }, [map]);

  return { onDragStart, onZoomChanged, showAt };
};
